import * as React from "react";
import { PageHeader } from "~/components/PageHeader";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { Badge, BadgeVariants } from "~/components/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor } from "~/components/Anchor";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { PlatformUserEvents } from "~/modules/analytics/events/platforms";
import { addUtmTrackingToUrl } from "~/utils/addUtmTrackingToUrl";
import { Protocol } from "~/modules/protocols_v4/types";

// // // //

/**
 * Component that displays a description with truncation and expand/collapse functionality
 */
function DescriptionDisplay(props: {
    description: string | null;
    threshold?: number;
}) {
    const { description, threshold = 400 } = props;
    const [isExpanded, setIsExpanded] = React.useState(false);

    if (!description) {
        return null;
    }

    const shouldTruncate = description.length > threshold;
    const displayDescription =
        shouldTruncate && !isExpanded
            ? description.slice(0, threshold) + "..."
            : description;

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="mt-3">
            <p className="font-light text-sm text-slate-600 max-w-4xl">
                {displayDescription}
                {shouldTruncate && (
                    <button
                        onClick={handleToggle}
                        className="ml-2 text-sm text-link hover:text-link-hover font-medium transition-colors"
                        aria-label={
                            isExpanded
                                ? "Show less description"
                                : "Show more description"
                        }
                    >
                        {isExpanded ? "View less" : "Read more"}
                    </button>
                )}
            </p>
        </div>
    );
}

/**
 * Header component for platform pages displaying platform info and description
 */
export function BroadridgeHeader(props: { broadridgeProtocol: Protocol }) {
    const { broadridgeProtocol } = props;

    return (
        <>
            <PageHeader
                title={<>{broadridgeProtocol.name}</>}
                icon={
                    <ImageLoader
                        src={broadridgeProtocol.icon_url || RWA_COIN_ICON}
                        alt={`${broadridgeProtocol.name} Icon`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-10 w-10 min-w-10 rounded-full"
                    />
                }
            />

            <div className="flex items-center gap-3 flex-wrap">
                {broadridgeProtocol.website !== null && (
                    <Anchor
                        href={addUtmTrackingToUrl(broadridgeProtocol.website)}
                        openInNewTab
                        analyticsLabel={
                            PlatformUserEvents.ClickIndividualWebsite
                        }
                    >
                        <Badge
                            label={
                                <>
                                    Website
                                    <FontAwesomeIcon
                                        icon={faExternalLink}
                                        className="h-3 ml-1.5"
                                    />
                                </>
                            }
                            variant={BadgeVariants.blue}
                            hover
                            className="px-3 py-0.5 tracking-wider"
                            size="md"
                        />
                    </Anchor>
                )}
            </div>

            <DescriptionDisplay description={broadridgeProtocol.description} />
        </>
    );
}
